// Global decimal place formatting rules for Batemo Insights
// Systematically applies consistent precision across all displays

/**
 * Format a numeric value based on its unit/property type
 * @param {number} value - The numeric value to format
 * @param {string} unit - The unit string (e.g., 'V', 'mΩ', 'Ah', '°C', 'W')
 * @param {string} [property] - Optional property name for special cases (e.g., 'CAP_fade', 'CAP_deviation')
 * @returns {string} Formatted value with appropriate decimal places
 */
export function formatValue(value, unit, property = null) {
  if (value === null || value === undefined || isNaN(value)) return 'N/A'
  
  // Special cases based on property name (takes precedence)
  if (property && typeof property === 'string') {
    const propLower = property.toLowerCase()
    
    // 3 decimal places
    if (propLower.includes('cap_fade') && propLower.includes('abs')) {
      return value.toFixed(3)
    }
    if (propLower.includes('cap_deviation') && propLower.includes('abs')) {
      return value.toFixed(3)
    }
    if (propLower.includes('capacity_fade') && propLower.includes('absolute')) {
      return value.toFixed(3)
    }
    if (propLower.includes('capacity_deviation') && propLower.includes('absolute')) {
      return value.toFixed(3)
    }
  }
  
  // Unit-based rules
  const unitLower = unit.toLowerCase()
  
  // 2 decimal places
  if (
    unitLower.includes('v') ||           // Voltage
    unitLower.includes('ω') ||           // Resistance (Ω, mΩ)
    unitLower.includes('ohm') ||         // Resistance
    unitLower.includes('ah') ||          // Capacity
    unitLower.includes('cm³') ||         // Volume (for impedance deviation/growth calculations)
    unitLower.includes('l') ||           // Volume
    (unitLower === '%' && property?.toLowerCase().includes('impedance')) ||  // Impedance deviation/growth relative
    (unitLower === '%' && property?.toLowerCase().includes('capacity') && property?.toLowerCase().includes('rel'))  // Capacity fade/deviation relative
  ) {
    return value.toFixed(2)
  }
  
  // 1 decimal place
  if (
    unitLower.includes('°c') ||          // Temperature
    unitLower.includes('c') ||           // Temperature (shorthand)
    unitLower.includes('k') && !unitLower.includes('kg') ||  // Kelvin
    unitLower === '%' ||                 // Efficiency (general percentages)
    unitLower.includes('a') && !unitLower.includes('ah') ||  // Current (but not Ah)
    unitLower.includes('w') && !unitLower.includes('wh') ||  // Power (but not Wh)
    unitLower.includes('wh') ||          // Energy
    unitLower.includes('g') ||           // Weight
    unitLower.includes('kg') ||          // Weight
    unitLower.includes('mm') ||          // Dimensions
    unitLower.includes('cm') && !unitLower.includes('cm³') ||  // Surface/dimensions (but not volume)
    unitLower.includes('m²')             // Surface area
  ) {
    return value.toFixed(1)
  }
  
  // Default: 2 decimal places for safety
  return value.toFixed(2)
}

/**
 * Format a KPI value for display with automatic decimal place rules
 * @param {number} value - The numeric value
 * @param {string} unit - The unit
 * @param {string} [kpiId] - Optional KPI identifier (e.g., 'CAP_fade_abs', 'eta_dc_25')
 * @returns {string} Formatted value
 */
export function formatKPI(value, unit, kpiId = null) {
  return formatValue(value, unit, kpiId)
}

/**
 * Get the number of decimal places for a given unit/property
 * Useful for chart axis formatting
 * @param {string} unit - The unit string
 * @param {string} [property] - Optional property name
 * @returns {number} Number of decimal places
 */
export function getDecimalPlaces(unit, property = null) {
  // Test with a sample value and count decimals in result
  const formatted = formatValue(1.23456789, unit, property)
  if (formatted === 'N/A') return 2
  
  const parts = formatted.split('.')
  return parts.length > 1 ? parts[1].length : 0
}

/**
 * Format an array of values for tooltips/charts
 * @param {Array<number>} values - Array of numeric values
 * @param {string} unit - The unit
 * @param {string} [property] - Optional property name
 * @returns {Array<string>} Array of formatted strings
 */
export function formatValueArray(values, unit, property = null) {
  return values.map(v => formatValue(v, unit, property))
}

// Export decimal place rules for documentation
export const DECIMAL_RULES = {
  '2_decimals': [
    'Voltage (V)',
    'Resistance (Ω, mΩ)',
    'Capacity (Ah, Ah/kg, Ah/L)',
    'Impedance Deviation (absolute)',
    'Impedance Growth (absolute)',
    'Volume (cm³, L)',
    'Impedance Deviation (relative, %)',
    'Impedance Growth (relative, %)',
    'Capacity Fade (relative, %)',
    'Capacity Deviation (relative, %)'
  ],
  '1_decimal': [
    'Temperature (°C, K)',
    'Efficiency (%)',
    'Current (A, A/kg, A/L)',
    'Power (W, W/kg, W/L)',
    'Energy (Wh, Wh/kg, Wh/L)',
    'Weight (g, kg)',
    'Surface (cm², m²)',
    'Dimensions (mm, cm, r/x/y/z)'
  ],
  '3_decimals': [
    'Capacity Fade (absolute)',
    'Capacity Deviation (absolute)'
  ]
}
